import { useNetwork } from '@/presentation/context/network';
import Modal from '../../modules/Modal';
import { sepolia } from 'wagmi/chains';
import Icon, { Icons } from '../../elements/Icon';
import Button from '../../elements/Button';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { Address, useContractWrite, useWaitForTransaction } from 'wagmi';
import mintNftContractAbi from '@/infrastructure/faucet/mintNftContractAbi';
import { useState } from 'react';
import useAddress from '@/application/read/web3/useAddress';
import { ActionItem, ActionList } from '../../modules/ActionList';
import { TransactionState } from '@nftx/react';
import { useTranslations } from 'use-intl';

const collections = [
  {
    name: 'Pudgy Penguins',
    blockExplorerUrl:
      'https://sepolia.etherscan.io/address/0xa807e2a221C6dAAFE1b4A3ED2dA5E8A53fDAf6BE',
    address: '0xa807e2a221C6dAAFE1b4A3ED2dA5E8A53fDAf6BE',
  },
  {
    name: 'Doodles',
    blockExplorerUrl:
      'https://sepolia.etherscan.io/address/0x49D3B3c0D5252E8c8b4331885479880847287a92',
    address: '0x49D3B3c0D5252E8c8b4331885479880847287a92',
  },
  {
    name: 'Cool Cats',
    blockExplorerUrl:
      'https://sepolia.etherscan.io/address/0x3d7E741B5E806303ADbE0706c827d3AcF0696516',
    address: '0x3d7E741B5E806303ADbE0706c827d3AcF0696516',
  },
  {
    name: 'Azuki',
    blockExplorerUrl:
      'https://sepolia.etherscan.io/address/0x7b2a53DAF97Ea7aa31B646e079C4772b6198aE9B',
    address: '0x7b2a53DAF97Ea7aa31B646e079C4772b6198aE9B',
  },
  {
    name: 'CryptoPunks',
    blockExplorerUrl:
      'https://sepolia.etherscan.io/address/0x67523b71bc3eef7e5d90492aed7a4b447bc1decd',
    address: '0x67523b71bc3eef7e5d90492aed7a4b447bc1decd',
  },
  {
    name: 'Milady',
    blockExplorerUrl:
      'https://sepolia.etherscan.io/address/0xea9af8dbdde2a8d3515c3b4e446ecd41afedb1c6',
    address: '0xea9af8dbdde2a8d3515c3b4e446ecd41afedb1c6',
  },
] as const;

const CollectionItem = ({
  name,
  address,
  index,
}: {
  name: string;
  blockExplorerUrl: string;
  address: Address;
  index: number;
}) => {
  const { write, data } = useContractWrite({
    abi: mintNftContractAbi,
    functionName: 'mint',
    args: [10n],
    address,
  });

  const { status: txStatus } = useWaitForTransaction({
    hash: data?.hash,
  });

  const status: TransactionState = (() => {
    switch (txStatus) {
      case 'idle':
        return 'None';
      case 'loading':
        return 'Mining';
      case 'error':
        return 'Fail';
      case 'success':
        return 'Success';
      default:
        return 'None';
    }
  })();

  return (
    <ActionItem
      key={address}
      action={write}
      index={index}
      label={`Mint ${name} Sepolia`}
      status={status}
    />
  );
};

const FaucetModal = () => {
  const { chain } = useNetwork();
  const [success, showSuccess] = useState(false);
  const address = useAddress();
  const [showMint, setShowMint] = useState(false);
  const t = useTranslations();

  return (
    <Modal size="2xl" clickOffToClose={true}>
      <div className="p-6 bg-gray-50">
        <h1 className="font-black text-xl mb-4">{t('FaucetModal.title')}</h1>
        <div className="bg-gray-100 rounded-lg p-4 text-center space-y-4">
          <h2 className="uppercase font-bold text-sm bg-clip-text text-transparent bg-gradient-pink-orange">
            {t('FaucetModal.text')}
          </h2>
          <p className="font-medium">{t('FaucetModal.tokens')}</p>
          <ul className="space-y-2">
            <li className="bg-gray-50 border-gray-300 p-3 rounded-md flex items-center">
              {t('FaucetModal.switch')}
              <span className="ml-auto">
                {chain.id === sepolia.id ? (
                  <ConnectButton.Custom>
                    {({
                      openChainModal,
                      openConnectModal,
                      chain: rainbowChain,
                      account,
                    }) => {
                      if (!account) {
                        return (
                          <Button onClick={openConnectModal}>
                            {t('Connect.connect')}
                          </Button>
                        );
                      }
                      if (rainbowChain?.id !== chain.id) {
                        return (
                          <Button onClick={openChainModal}>
                            {t('Connect.changeNetwork')}
                          </Button>
                        );
                      }

                      return (
                        <Icon
                          name={Icons.CHECK_CIRCLE}
                          className="text-green-600"
                        />
                      );
                    }}
                  </ConnectButton.Custom>
                ) : (
                  <Button href="/sepolia/">{t('FaucetModal.loadApp')}</Button>
                )}
              </span>
            </li>
            <li className="bg-gray-50 border-gray-300 p-3 rounded-md flex items-center">
              <span className="text-left">{t('FaucetModal.getEth')}</span>
              <div className="flex flex-wrap justify-end ml-auto space-x-2">
                <Button
                  href="https://sepoliafaucet.com/"
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="truncate inline-flex"
                  size="xs"
                  variant="tertiary"
                >
                  {t('FaucetModal.alchemy')}
                  <Icon
                    name={Icons.EXTERNAL}
                    size={16}
                    className="ml-2 flex-none"
                  />
                </Button>

                <Button
                  href="https://faucet.quicknode.com/ethereum/sepolia/"
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="truncate inline-flex"
                  size="xs"
                  variant="tertiary"
                >
                  {t('FaucetModal.quicknode')}
                  <Icon
                    name={Icons.EXTERNAL}
                    size={16}
                    className="ml-2 flex-none"
                  />
                </Button>
              </div>
            </li>
            {!!address && (
              <li className="bg-gray-50 border-gray-300 p-3 rounded-md">
                {success ? (
                  <div className="">
                    <h2 className="text-lg text-center mb-2 font-bold">
                      {t('FaucetModal.ActionList.title')}
                    </h2>
                    <h3 className="text-gray-500 text-base text-center mb-4">
                      {t('FaucetModal.ActionList.description')}
                    </h3>
                    <p className="text-center my-6">
                      <Icon
                        name={Icons.CHECK_CIRCLE}
                        className="text-green-600 inline-flex"
                        size={48}
                      />
                    </p>
                    <p>
                      <Button href="/sepolia/profile" className="inline-flex">
                        {t('FaucetModal.explore')}
                      </Button>
                    </p>
                  </div>
                ) : showMint ? (
                  <ul className="w-full space-y-2">
                    <ActionList
                      onCancel={() => void 0}
                      onComplete={() => {
                        showSuccess(true);
                      }}
                      description={t('FaucetModal.ActionList.description')}
                      title={t('FaucetModal.ActionList.title')}
                    >
                      {collections.map((c, i) => {
                        return (
                          <CollectionItem {...c} index={i} key={c.address} />
                        );
                      })}
                    </ActionList>
                  </ul>
                ) : (
                  <div className="flex items-center">
                    <h4 className="text-left">{t('FaucetModal.mint')}</h4>
                    <Button
                      onClick={() => setShowMint(true)}
                      className="ml-auto"
                      size="small"
                    >
                      {t('FaucetModal.get')}
                    </Button>
                  </div>
                )}
              </li>
            )}
          </ul>
        </div>
      </div>
    </Modal>
  );
};

export default FaucetModal;
